import React, {useContext, useId} from "react";
import {Avatar as PrimeAvatar} from "primereact/avatar";
import {Tooltip} from "primereact/tooltip";
import {useSelector} from "react-redux";
import {resolveMediaUrl} from "../app-config";
import {RealtimeContext} from "../realtime/realtime-provider";
import {useOptionalFocusSession} from "../focus/focus-session-context";
import {useTranslate} from "../useTranslate";

export function isCurrentUserAvatar(memberData, currentUser) {
    const member = memberData?.member || memberData;
    return Boolean(
        (member?.id && currentUser?.id && String(member.id) === String(currentUser.id))
        || (member?.documentId && currentUser?.documentId && String(member.documentId) === String(currentUser.documentId))
    );
}

export function usesCompactPresenceMarker(size) {
    return !["large", "xlarge"].includes(size);
}

export function presetAvatarUrl(filename) {
    const safeFilename = String(filename || "").replace(/[^a-zA-Z0-9._-]/g, "");
    return safeFilename ? `${process.env.PUBLIC_URL || ""}/avatars/${safeFilename}` : null;
}

function Avatar({memberData, size = "normal", bgColorRange = 100, borderColorRange = 300, onClick, className = "text-2xs font-bold", style, shape = "circle", unStyled, tooltip = false, fallbackPreset}) {
    const translate = useTranslate();
    const currentUser = useSelector((state) => state.auth.user);
    const realtime = useContext(RealtimeContext);
    const focus = useOptionalFocusSession();
    const tooltipTargetId = `member-avatar-${useId().replace(/:/g, "")}`;
    const tooltipProps = tooltip ? {
        id: tooltipTargetId,
        "data-pr-tooltip": memberData?.fullname,
    } : {};

    if (!memberData) return null;

    const abbreviation = `${memberData.firstname?.charAt(0) ?? ""} ${memberData.lastname?.charAt(0) ?? ""}`;
    const commonProps = {
        ...tooltipProps,
        style,
        shape,
        size,
        onClick,
    };
    const image = memberData.profileImage
        ? resolveMediaUrl(memberData.profileImage.avatarUrl || memberData.profileImage.url)
        : presetAvatarUrl(memberData.presetAvatar || fallbackPreset);
    const styledClassName = image
        ? `member-name-${memberData.id} border-none border-${memberData.color}-${borderColorRange}`
        : `member-name bg-${memberData.color}-${bgColorRange} border-1 border-${memberData.color}-${borderColorRange}`;
    const avatarClassName = `${unStyled ? `member-name-${memberData.id}` : styledClassName} ${onClick ? "cursor-pointer" : ""} ${className}`;
    const online = Boolean(realtime?.isMemberOnline?.(memberData));
    const focusStatus = focus?.getMemberFocusStatus?.(memberData)
        || (focus?.isMemberFocusing?.(memberData) ? "running" : null);
    const focusing = online && focusStatus === "running";
    const resting = online && focusStatus === "paused";
    const presenceStatus = focusing ? "focusing" : resting ? "resting" : online ? "online" : "offline";
    const presenceColor = focusing ? "bg-red-500" : resting ? "bg-orange-500" : online ? "bg-green-500" : "bg-gray-400";
    const showPresence = Boolean(realtime && realtime.status !== "idle" && !isCurrentUserAvatar(memberData, currentUser));
    const presenceSizeClass = usesCompactPresenceMarker(size) ? "block p-1 border-1" : "w-1rem h-1rem border-2";

    return <>
        {tooltip && <Tooltip target={`#${tooltipTargetId}`} className="text-2xs"/>}
        <span className="relative inline-flex flex-shrink-0">
            <PrimeAvatar
                {...commonProps}
                {...(image ? {image} : {label: abbreviation})}
                className={avatarClassName}
            />
            {showPresence && <span
                role="status"
                aria-label={translate(`presence.${presenceStatus}`)}
                title={translate(`presence.${presenceStatus}`)}
                className={`absolute bottom-0 right-0 border-circle border-white ${presenceSizeClass} ${presenceColor}`}
            />}
        </span>
    </>;
}

export default Avatar;
